import * as React from 'react';
import i18n from '@/utils/i18n';
import { ItemTypeCategory } from '@/graphql/generatedTypes';
import { useAllItemTypesQuery } from '@/graphql/queries/allItemTypes.generated';
import { isOrderableStoreAssetItemType } from '@/utils/orderableStoreAssets';
import { ResponsiveTable } from '../Layout';
import { ContentSection } from '../Layout/ContentSection';
import { StoreAssetOrderItem } from './HandleStatusChange';
import { orderTypeAllowsStoreAssets } from './storeAssetOrderTypes';

/**
 * Store-asset lines on an ops order. Only the STORE_ASSET ItemTypes in
 * ORDERABLE_STORE_ASSET_ITEM_TYPE_IDS are offered.
 *
 * This screen only ever creates an order — the one route mounting
 * EditMerchantOrder is /create-order/:vytalId, with no :orderId — so there is no
 * loaded line an unlisted type could need a row for.
 *
 * Deliberately NOT part of ItemTable: that table indexes rows by
 * (status, typeId) via getItemIndex, and every store-asset row has typeId 0, so
 * they would all collide on one index (and with untracked rows, which are
 * typeId <= 0). Holding them in their own collection keyed by item type is the
 * same split colugo uses on OpsOrderUpdateInput.
 */
export const StoreAssetTable = function StoreAssetTable(props: {
  orderType: string | number;
  storeAssetItems: StoreAssetOrderItem[];
  setStoreAssetItems: React.Dispatch<
    React.SetStateAction<StoreAssetOrderItem[]>
  >;
  disabled: boolean;
}) {
  const { orderType, storeAssetItems, setStoreAssetItems, disabled } = props;

  const { data, loading, error } = useAllItemTypesQuery({
    variables: {
      filter: { category: ItemTypeCategory.StoreAsset, archived: false },
      take: 500,
    },
    fetchPolicy: 'cache-first',
  });

  const itemTypes = React.useMemo(
    () =>
      [...(data?.itemTypes?.items ?? [])]
        .filter((itemType) => isOrderableStoreAssetItemType(itemType.id))
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [data],
  );

  function setAmount(itemTypeId: number, name: string, value: string) {
    const amount = value.replace(/[^0-9]/g, '');
    setStoreAssetItems((current) => {
      const next = [...current];
      const index = next.findIndex((i) => i.itemTypeId === itemTypeId);
      if (index === -1) {
        next.push({ itemTypeId, name, amount });
      } else {
        next[index] = { ...next[index], amount };
      }
      return next;
    });
  }

  function amountFor(itemTypeId: number): string | number {
    return (
      storeAssetItems.find((i) => i.itemTypeId === itemTypeId)?.amount ?? '0'
    );
  }

  if (!orderTypeAllowsStoreAssets(orderType)) {
    return null;
  }

  return (
    <ContentSection title={i18n.t('MerchantOrder:storeAssets')}>
      {loading && <p className="mt-4">{i18n.t('Common:loading')}</p>}
      {error && <p className="mt-4">{i18n.t('Common:error')}</p>}
      {!loading && !error && itemTypes.length === 0 && (
        <p className="mt-4">{i18n.t('MerchantOrder:noStoreAssets')}</p>
      )}
      {itemTypes.length > 0 && (
        <ResponsiveTable className="mt-4" fixed={true}>
          <thead>
            <tr>
              <th>{i18n.t('Common:name')}</th>
              <th>{i18n.t('MerchantOrder:delivery')}</th>
            </tr>
          </thead>
          <tbody>
            {itemTypes.map((itemType) => (
              <tr key={itemType.id}>
                <td>{itemType.name}</td>
                <td>
                  <input
                    className="form-control"
                    value={amountFor(itemType.id)}
                    disabled={disabled}
                    onChange={(e) =>
                      setAmount(
                        itemType.id,
                        itemType.name ?? String(itemType.id),
                        e.target.value,
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
    </ContentSection>
  );
};
